import React, {
  useEffect, useLayoutEffect, useRef, useState,
} from 'react';

import { v4 as uuidv4 } from 'uuid';
import { useSelector } from 'react-redux';
import { useFetchye } from 'fetchye';
import { useIntl } from 'react-intl';
import { AnimatePresence } from 'framer-motion';
import InputComponent from './InputComponent';
import styles from '../styles/PageWrapper.scss';
import { streamTravelWishlist } from '../utils/streamableApi';
import { ChatLanding } from './ChatLanding';
import {
  ERROR, IDLE, LOADING, MODULE_NAME, SUCCESS,
} from '../utils/constants';
import { ChatDisplay } from './ChatDisplay';
import { CreateWishlistModal } from './CreateWishlistModal';
import { LoadingStep } from '../utils/streamableHelpers';

const uuid = uuidv4();

const PageWrapper = () => {
  const intl = useIntl();
  const [messageArray, setMessageArray] = useState([]);
  const [loadingStep, setLoadingStep] = useState(LoadingStep.FINDING);
  const [conversationApiStatus, setConversationApiStatus] = useState(IDLE);
  const messagesRef = useRef(null);
  const selectedHotels = useSelector((state) => state.getIn(['modules', MODULE_NAME, 'selectedHotels'])).toJS();
  const [createModalIsOpen, setCreateModalIsOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars -- test
  const [saveSuccess, setSaveSuccess] = useState(false);
  const apiUrl = useSelector((state) => state.getIn(['config', 'apiUrl']));
  // eslint-disable-next-line no-unused-vars -- test
  const { isLoading, data, error } = useFetchye(`${apiUrl}/session`, {
    credentials: 'include',
    headers: {
      x_amex_tracking_id: uuid,
    },
    method: 'POST',
  });

  const [canSendMessage, setCanSendMessage] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setCanSendMessage(true);
    }
  }, [isLoading]);

  const idCounter = useRef(0);
  // eslint-disable-next-line no-plusplus -- testing
  const nextId = () => ++idCounter.current;

  const sendMessage = async (message) => {
    if (canSendMessage) {
      const newMessage = {
        id: nextId(),
        type: 'message',
        isSentByUser: true,
        message,
      };
      setMessageArray([...messageArray, newMessage]);
      setConversationApiStatus(LOADING);

      try {
        const result = await streamTravelWishlist({
          apiUrl,
          conversationId: data?.body?.conversation_id,
          message,
          intl,
          onStepChange: (step) => {
            setLoadingStep(step);
          },
        });

        setConversationApiStatus(SUCCESS);

        const apiMessage = {
          id: nextId(),
          type: 'message',
          isSentByUser: false,
          message: result.agentMessage,
        };

        setMessageArray((prev) => {
          const nextMessages = [...prev, apiMessage];

          if (result.hotels.length > 0) {
            nextMessages.push({
              id: nextId(),
              type: 'hotels',
              hotels: result.hotels,
            });
          }

          return nextMessages;
        });
      } catch {
        setConversationApiStatus(ERROR);
      }
    }
  };

  // local dev for hotel testing
  // useEffect(() => {
  //   setMessageArray(mockMessageArray);
  // }, []);

  const saveSelectedHotels = async () => {
    const payload = {
      conversation_id: data?.body?.conversation_id,
      selected_hotels: selectedHotels.map((hotel) => ({
        hotel_id: hotel.hotelId,
        name: hotel.name,
        address: hotel.address || null,
        description: hotel.description || null,
      })),
    };
    try {
      await fetch(`${apiUrl}/save`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          x_amex_tracking_id: uuidv4(),
          x_conversation_id: data?.body?.conversation_id,
        },
        body: JSON.stringify(payload),
      });
      setSaveSuccess(true);
    } catch {
      setSaveSuccess(false);
    }
  };

  /* istanbul ignore next */
  const scrollToBottom = () => {
    const el = messagesRef.current;
    if (!el) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    });
  };

  useLayoutEffect(() => {
    scrollToBottom();
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    });

    return () => cancelAnimationFrame(id);
  }, [messageArray]);

  return (
    <>
      <div className={`${styles.bggradient} height-full flex flex-column`}>
        <div
          ref={messagesRef}
          className="container flex flex-column flex-item-grow pad-2-t scroll-y"
        >
          <h1 className="sr-only">Chat page for ai wishlist builder</h1>
          {messageArray.length === 0 && (
          <ChatLanding sendMessage={sendMessage} />
          )}
          {messageArray.length > 0
        && (
          <ChatDisplay
            messageArray={messageArray}
            conversationApiStatus={conversationApiStatus}
            loadingStep={loadingStep}
          />
        )}
        </div>
        <InputComponent
          conversationApiStatus={conversationApiStatus}
          sendMessage={sendMessage}
          selectedHotels={selectedHotels}
          setCreateModalIsOpen={setCreateModalIsOpen}
        />
      </div>
      <AnimatePresence>
        {createModalIsOpen && (
        <CreateWishlistModal
          setCreateModalIsOpen={setCreateModalIsOpen}
          saveSelectedHotels={saveSelectedHotels}
        />
        )}
      </AnimatePresence>
    </>
  );
};

export default PageWrapper;
